"use client";

import { useEffect, useState } from "react";
import { useLanguage } from "../../app/components/context/LanguageContext";
import EventCard from "../components/EventCard";
import { useTheme } from "../context/ThemeContext";
import { STRAPI_URL } from "@/lib/strapi";

type Event = {
  id: number;
  Title: string;
  slug: string;
  typeEvent: string;
  date: string;
  photocover: { url: string | null; alternativeText: string };
  description?: { type: string; children: { text: string }[] }[];
};

const ITEMS_PER_PAGE = 4;

export default function EventSection() {
  const { language } = useLanguage();
  const { theme } = useTheme();

  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);

 // const STRAPI_URL = process.env.NEXT_PUBLIC_STRAPI_URL;

  /* ================= FETCH EVENTS ================= */
  const fetchEvents = async (lang: string) => {
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(
        `${STRAPI_URL}/api/eventsses?populate=*&locale=${lang}`,
        { cache: "no-store" }
      );

      const data = await res.json();

      const mappedEvents: Event[] = data.data.map((item: any) => ({
        id: item.id,
        Title: item.Title,
        slug: item.slug,
        typeEvent: item.typeEvent,
        date: item.date,
        description: item.description || [],
        photocover: {
          url: item.photocover?.formats?.medium?.url
            ? `${STRAPI_URL}${item.photocover.formats.medium.url}`
            : item.photocover?.url
            ? `${STRAPI_URL}${item.photocover.url}`
            : null,
          alternativeText: item.photocover?.alternativeText || "Event image",
        },
      }));

      const sorted = mappedEvents.sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
      );

      setEvents(sorted);
    } catch (err) {
      console.error(err);
      setError("Failed to load events.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents(language);
  }, [language]);

  /* Reset page on filter / language change */
  useEffect(() => {
    setCurrentPage(1);
  }, [activeFilter, language]);

  /* ================= FILTER ================= */
  const filteredEvents = events.filter((event) => {
    if (activeFilter === "all") return true;
    if (activeFilter === "upcoming") return new Date(event.date) >= new Date();
    if (activeFilter === "past") return new Date(event.date) < new Date();
    return event.typeEvent === activeFilter;
  });

  /* ================= PAGINATION ================= */
  const totalPages = Math.ceil(filteredEvents.length / ITEMS_PER_PAGE);

  const paginatedEvents = filteredEvents.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  /* ================= COUNTS ================= */
const eventTypeCounts: Record<string, number> = events.reduce(
  (acc, e) => {
    acc[e.typeEvent] = (acc[e.typeEvent] || 0) + 1;
    return acc;
  },
  {} as Record<string, number>
);

  /* ================= LOADING ================= */
  if (loading)
    return (
      <section className={`min-h-screen py-20 transition-colors duration-500 ${
        theme === "dark" ? "bg-gradient-to-br from-gray-900 to-blue-900" : "bg-gradient-to-br from-slate-50 to-blue-50"
      }`}>
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <div className="animate-pulse">
              <div className={`h-12 rounded-lg w-64 mx-auto mb-6 ${
                theme === "dark" ? "bg-gray-700" : "bg-gray-200"
              }`}></div>
              <div className={`h-6 rounded w-96 mx-auto ${
                theme === "dark" ? "bg-gray-600" : "bg-gray-200"
              }`}></div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[...Array(4)].map((_, index) => (
              <div key={index} className="animate-pulse">
                <div className={`rounded-2xl h-80 ${
                  theme === "dark" ? "bg-gray-700" : "bg-gray-200"
                }`}></div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );

  /* ================= ERROR ================= */
  if (error)
    return (
      <section className={`min-h-screen py-20 transition-colors duration-500 ${
        theme === "dark" ? "bg-gradient-to-br from-gray-900 to-blue-900" : "bg-gradient-to-br from-slate-50 to-blue-50"
      }`}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className={`rounded-2xl p-12 backdrop-blur-sm border ${
            theme === "dark" 
              ? "bg-gray-800/80 border-gray-700" 
              : "bg-white/80 border-gray-200"
          }`}>
            <div className="text-6xl mb-6">😔</div>
            <h3 className={`text-2xl font-bold mb-4 ${
              theme === "dark" ? "text-white" : "text-gray-900"
            }`}>
              {language === "fr" && "Erreur de chargement"}
              {language === "en" && "Loading Error"}
              {language === "ar" && "خطأ في التحميل"}
            </h3>
            <p className={`mb-8 ${
              theme === "dark" ? "text-gray-400" : "text-gray-600"
            }`}>{error}</p>
            <button
              onClick={() => fetchEvents(language)}
              className={`px-8 py-3 rounded-xl font-semibold transition-all duration-300 ${
                theme === "dark"
                  ? "bg-blue-600 hover:bg-blue-700 text-white"
                  : "bg-blue-600 hover:bg-blue-700 text-white"
              }`}
            >
              {language === "fr" && "Réessayer"}
              {language === "en" && "Try Again"}
              {language === "ar" && "إعادة المحاولة"}
            </button>
          </div>
        </div>
      </section>
    );


  /* ================= UI ================= */
  return (
   <section className={`min-h-screen py-20 transition-colors duration-500 ${
      theme === "dark" ? "bg-gradient-to-br from-gray-900 to-blue-900" : "bg-gradient-to-br from-slate-50 to-blue-50"
    } relative overflow-hidden`}>
          <div className={`absolute top-0 right-0 w-96 h-96 rounded-full -translate-y-48 translate-x-48 transition-all duration-700 ${
        theme === "dark" ? "bg-blue-900/20" : "bg-blue-200/30"
      }`}></div>
            <div className={`absolute bottom-0 left-0 w-80 h-80 rounded-full -translate-x-40 translate-y-40 transition-all duration-700 ${
        theme === "dark" ? "bg-purple-900/20" : "bg-purple-200/30"
      }`}></div>

      <div className="max-w-6xl mx-auto px-4">
   <div className="text-center mb-16">
          <div className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold mb-6 transition-colors duration-500 ${
            theme === "dark" 
              ? "bg-blue-900/30 text-blue-400" 
              : "bg-blue-100 text-blue-600"
          }`}>
            <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
            </svg>
            {language === "fr" && "Événements à venir"}
            {language === "en" && "Upcoming Events"}
            {language === "ar" && "الفعاليات القادمة"}
          </div>

          <h2 className={`text-4xl md:text-6xl font-bold mb-6 transition-colors duration-500 ${
            theme === "dark" ? "text-white" : "text-gray-900"
          }`}>
            {language === "fr" && "Nos Événements"}
            {language === "en" && "Our Events"}
            {language === "ar" && "فعالياتنا"}
          </h2>

          <p className={`text-xl max-w-2xl mx-auto leading-relaxed transition-colors duration-500 ${
            theme === "dark" ? "text-white" : "text-gray-900"
          }`}>
            {language === "fr" && "Découvrez nos prochains événements et rejoignez-nous pour des expériences uniques."}
            {language === "en" && "Discover our upcoming events and join us for unique experiences."}
            {language === "ar" && "اكتشف فعالياتنا القادمة وانضم إلينا لتجارب فريدة."}
          </p>





 
         </div>

        {/* FILTER BUTTONS */}
        <div className="flex justify-center gap-4 mb-12">
          {[
            { key: "all", label: { fr: "Tous", en: "All", ar: "الكل" } },
            { key: "upcoming", label: { fr: "À venir", en: "Upcoming", ar: "القادمة" } },
            { key: "past", label: { fr: "Passés", en: "Past", ar: "الماضية" } },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setActiveFilter(key)}
              className={`px-6 py-3 rounded-xl font-semibold ${
                activeFilter === key
                  ? "bg-blue-600 text-white"
                  : "border"
              }`}
            >
              {label[language as keyof typeof label]}
            </button>
          ))}
        </div>

        {/* EVENTS GRID */}
        {paginatedEvents.length === 0 ? (
          <p className="text-center">No events</p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {paginatedEvents.map((event) => (
              <div
                key={event.id}
                className={`rounded-2xl border p-6 transition hover:scale-105 ${
                  theme === "dark"
                    ? "bg-gray-800 border-gray-700"
                    : "bg-white border-gray-200"
                }`}
              >
                <EventCard event={event} />
              </div>
            ))}
          </div>
        )}

        {/* PAGINATION */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-4 mt-12">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
              className={`px-6 py-3 rounded-xl ${
                currentPage === 1
                  ? "opacity-50 cursor-not-allowed"
                  : "border"
              }`}
            >
              {language === "fr" && "Précédent"}
              {language === "en" && "Previous"}
              {language === "ar" && "السابق"}
            </button>

            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
              className={`px-6 py-3 rounded-xl ${
                currentPage === totalPages
                  ? "opacity-50 cursor-not-allowed"
                  : "bg-gray-900 text-white"
              }`}
            >
              {language === "fr" && "Suivant"}
              {language === "en" && "Next"}
              {language === "ar" && "التالي"}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}





















 